import { getHexTileMapKey } from '@/game/hexmap/model/keys';
import { groundPoint, groundTileKey } from '@/game/hexmap/view/groundPick';
import type { RuleSet } from '@/game/rules';
import { isCity, type PlayerId, type SimState } from '@/game/sim';

/**
 * Left-click resolution between units and own buildings. A unit model stands
 * up off its tile, so from the tilted camera it can cover a neighbouring
 * building; the tile under the cursor decides instead of which mesh is nearer.
 */
export interface ClickTiles {
  /** Every tile of an own building that a left-click selects (footprints included). */
  buildings: ReadonlySet<string>;
  /** Own completed city tiles: the city wins there even over a unit on it. */
  cities: ReadonlySet<string>;
}

/** The own buildings' clickable tiles (completed ones, and sites other than cities). */
export function ownClickTiles(state: SimState, rules: RuleSet, playerId: PlayerId): ClickTiles {
  const buildings = new Set<string>();
  const cities = new Set<string>();
  for (const b of Object.values(state.buildings)) {
    if (b.owner !== playerId) continue;
    const city = isCity(b, rules);
    if (b.buildProgress < 1 && city) continue;
    for (const c of [b.pos, ...(b.footprint ?? [])]) buildings.add(getHexTileMapKey(c));
    if (city && b.buildProgress >= 1) cities.add(getHexTileMapKey(b.pos));
  }
  return { buildings, cities };
}

interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/**
 * The map tile the ray (e.g. a pointer event's) meets at ground level: the top
 * of land tiles. The same plane the board's tile picker hits (`HexMap`).
 */
export function cursorTileKey(ray: { origin: Vec3; direction: Vec3 }): string | null {
  const hit = groundPoint(ray);
  return hit && groundTileKey(hit.x, hit.z);
}

/**
 * Whether a unit on `unitKey` takes a left-click that hit its model, with
 * the cursor over `cursorKey`. On an own city tile the city always wins; over
 * any other own building tile the building wins unless the unit stands still
 * on that very tile: a worker only passing over its farm (leaving to haul, or
 * coming back) doesn't steal the farm's click.
 */
export function unitTakesClick(
  unitKey: string,
  cursorKey: string | null,
  tiles: ClickTiles,
  moving = false
): boolean {
  if (tiles.cities.has(unitKey)) return false;
  if (cursorKey === null || !tiles.buildings.has(cursorKey)) return true;
  return unitKey === cursorKey && !moving;
}

/** Whether a unit is on its way somewhere (its path not yet walked), for `unitTakesClick`. */
export function isMoving(unit: { path?: readonly unknown[] }): boolean {
  return (unit.path?.length ?? 0) > 0;
}
